/**
 * AnimatedCategoryPie — Iter-127
 *
 * Beautiful category value-share pie:
 *  - Each slice labelled with "Category — 42%" outside the ring
 *  - Hover / click a slice → it pops OUT of the ring (activeIndex)
 *  - Centre shows the active slice's value + percentage
 *  - Fallback: shows total value + count of categories when nothing selected
 *
 * Colours: the Heda chart series (chartTheme.js).
 */
import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, Sector, ResponsiveContainer } from 'recharts';
import { C, SERIES as COLORS } from './chartTheme';

const short = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const a = Math.abs(n);
  if (a >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (a >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n)}`;
};

// Recharts active-slice renderer — draws an outer arc that "pops out"
// and adds a small label with the percentage. Fed the payload from Recharts
// so every field we need (fill, midAngle, cx, cy, value, pct, category) is
// already available.
function renderActiveShape(props) {
  const {
    cx, cy, startAngle, endAngle, innerRadius, outerRadius, fill, payload,
  } = props;
  return (
    <g>
      {/* Popped-out arc — same slice, expanded outward */}
      <Sector
        cx={cx} cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle} endAngle={endAngle}
        fill={fill}
      />
      {/* Faint outer ring for depth */}
      <Sector
        cx={cx} cy={cy}
        startAngle={startAngle} endAngle={endAngle}
        innerRadius={outerRadius + 10}
        outerRadius={outerRadius + 12}
        fill={fill}
        opacity={0.3}
      />
    </g>
  );
}

export default function AnimatedCategoryPie({
  data = [],
  title = 'Value Share by Category',
  compact = false,
  testId = 'animated-category-pie',
}) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [narrow] = useState(() => window.matchMedia?.('(max-width: 640px)').matches ?? false);

  // Normalise + compute pct in case caller didn't supply.
  const chartData = useMemo(() => {
    const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
    return data.map((d, i) => ({
      ...d,
      color: COLORS[i % COLORS.length],
      pct: d.pct != null ? d.pct : Number(((d.value / total) * 100).toFixed(1)),
    }));
  }, [data]);

  const total = chartData.reduce((s, d) => s + d.value, 0);
  const activeSlice = activeIndex !== null ? chartData[activeIndex] : null;

  if (!chartData.length) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-heda-mute" data-testid={testId}>
        <span data-testid={testId + '-empty'}>No category data</span>
      </div>
    );
  }

  // Phones have no room beside the ring for "Category N%": show the % only,
  // the legend below names the categories.
  const pctOnly = compact || narrow;
  const outerR = compact ? 58 : 90;
  const innerR = compact ? 38 : 55;

  return (
    <div className="w-full" data-testid={testId}>
      <div className="relative" style={{ height: compact ? 240 : 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="category"
              cx="50%"
              cy="50%"
              innerRadius={innerR}
              outerRadius={outerR}
              paddingAngle={2}
              activeIndex={activeIndex === null ? undefined : activeIndex}
              activeShape={renderActiveShape}
              onClick={(_, idx) => setActiveIndex(activeIndex === idx ? null : idx)}
              onMouseEnter={(_, idx) => setActiveIndex(idx)}
              onMouseLeave={() => setActiveIndex(null)}
              isAnimationActive
              animationDuration={800}
              // Label = "Plywood 42%" placed outside the slice
              label={({ category, pct, cx, cy, midAngle, outerRadius }) => {
                const RAD = Math.PI / 180;
                const r = outerRadius + 14;
                const x = cx + r * Math.cos(-midAngle * RAD);
                const y = cy + r * Math.sin(-midAngle * RAD);
                if (pct < 3) return null; // skip tiny slice labels
                // Iter-129: compact mode = show ONLY "%" (labels were
                // overflowing on the side-by-side comparison pies).
                // Full-page mode still shows "Category N%" for context.
                const labelText = pctOnly ? `${pct}%` : `${category} ${pct}%`;
                return (
                  <text
                    x={x} y={y}
                    fill={C.ink}
                    fontSize={compact ? 10 : 11}
                    fontWeight={600}
                    textAnchor={x > cx ? 'start' : 'end'}
                    dominantBaseline="central"
                  >
                    {labelText}
                  </text>
                );
              }}
              labelLine={{ stroke: C.mute, strokeWidth: 0.6 }}
            >
              {chartData.map((d) => (
                <Cell key={d.category} fill={d.color} cursor="pointer" />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {/* Centre readout — active slice OR total */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <motion.div
            key={activeSlice ? activeSlice.category : 'total'}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
            className="text-center"
          >
            {activeSlice ? (
              <>
                <p className="text-[10px] uppercase tracking-wider text-heda-mute font-semibold" data-testid="pie-active-category">
                  {activeSlice.category}
                </p>
                <p className="font-display text-xl sm:text-2xl font-bold tabular-nums leading-tight" style={{ color: activeSlice.color }}>
                  {short(activeSlice.value)}
                </p>
                <p className="text-sm font-semibold" style={{ color: activeSlice.color }}>
                  {activeSlice.pct}%
                </p>
              </>
            ) : (
              <>
                <p className="text-[10px] uppercase tracking-wider text-heda-mute font-semibold">Total</p>
                <p className={`${compact ? 'text-base sm:text-xl' : 'text-xl sm:text-2xl'} font-display font-bold text-heda-ink tabular-nums leading-tight`}>
                  {short(total)}
                </p>
                {/* Kept short so it stays inside the ring on a phone; compact rings are too small for it. */}
                {!compact && <p className="text-[11px] text-heda-mute">{chartData.length} categories</p>}
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* Legend chip row — click to also pop the corresponding slice */}
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-1 text-[11px]" data-testid="pie-legend">
        {chartData.slice(0, 8).map((d, i) => (
          <button
            key={d.category}
            onClick={() => setActiveIndex(activeIndex === i ? null : i)}
            className={`inline-flex items-center gap-1 hover:opacity-80 transition-opacity ${
              activeIndex === i ? 'font-bold text-heda-ink' : 'text-heda-sub'
            }`}
            data-testid={`pie-legend-${d.category}`}
          >
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: d.color }} />
            <span>{d.category}</span>
            <span className="text-heda-mute">{d.pct}%</span>
          </button>
        ))}
      </div>
    </div>
  );
}
